// src/components/perfiles/FormularioEconomiaColaborativa.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { obtenerDepartamentos, obtenerMunicipios } from '../../actions/EntidadAction';

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': { borderColor: 'rgba(30, 136, 229, 0.3)', borderWidth: '2px' },
    '&:hover fieldset': { borderColor: '#1E88E5' },
    '&.Mui-focused fieldset': { borderColor: '#1E88E5', borderWidth: '2px' }
  },
  '& .MuiInputLabel-root': {
    color: '#757575',
    '&.Mui-focused': { color: '#1E88E5' }
  }
};

const FormularioEconomiaColaborativa = ({ perfil, onVolver }) => {
  const [paso, setPaso] = useState(1);
  const totalPasos = 4;
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [municipiosFiltrados, setMunicipiosFiltrados] = useState([]);

  const [formData, setFormData] = useState({
    nombre: '', apellido: '', email: '', phoneNumber: '',
    tipoDocumento: 'CC', documento: '',
    departamentoID: '', municipioID: '', zonaResidencia: '',
    profesionOficio: '', ocupacionActual: '', sectorEconomico: '',
    experienciaCooperativa: false, cooperativasPertenecio: '',
    areasInteres: [], disponibilidadHoraria: '',
    queEspera: '', habilidadesOfrecer: ''
  });

  useEffect(() => {
    cargarCatalogos();
  }, []);

  useEffect(() => {
    if (formData.departamentoID) {
      const municipiosDelDepartamento = municipios.filter(
        m => m.departamentoID === parseInt(formData.departamentoID)
      );
      setMunicipiosFiltrados(municipiosDelDepartamento);
    }
  }, [formData.departamentoID, municipios]);

  const cargarCatalogos = async () => {
    try {
      const deptosRes = await obtenerDepartamentos();
      if (deptosRes.status === 200) setDepartamentos(deptosRes.data);
      const munisRes = await obtenerMunicipios();
      if (munisRes.status === 200) setMunicipios(munisRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, valor) => {
    setFormData(prev => {
      const valores = prev[field] || [];
      if (valores.includes(valor)) {
        return { ...prev, [field]: valores.filter(v => v !== valor) };
      }
      return { ...prev, [field]: [...valores, valor] };
    });
  };

  const progreso = (paso / totalPasos) * 100;

  const renderPaso = () => {
    switch(paso) {
      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: perfil.color }}>
              📋 Datos Básicos
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField label="Nombre" value={formData.nombre} onChange={handleChange('nombre')} sx={textFieldStyles} required />
              <TextField label="Apellido" value={formData.apellido} onChange={handleChange('apellido')} sx={textFieldStyles} required />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField select label="Tipo Documento" value={formData.tipoDocumento} onChange={handleChange('tipoDocumento')} sx={textFieldStyles}>
                <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
                <MenuItem value="TI">Tarjeta de Identidad</MenuItem>
                <MenuItem value="CE">Cédula de Extranjería</MenuItem>
              </TextField>
              <TextField label="Número Documento" value={formData.documento} onChange={handleChange('documento')} sx={textFieldStyles} required />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Email" type="email" value={formData.email} onChange={handleChange('email')} sx={textFieldStyles} required />
              <TextField label="Teléfono" value={formData.phoneNumber} onChange={handleChange('phoneNumber')} sx={textFieldStyles} required />
            </Box>
          </Box>
        );
      
      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: perfil.color }}>
              📍 Ubicación
            </Typography>
            <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
              <strong>¿Por qué tu ubicación?</strong> Nos ayuda a conectarte con cooperativas cercanas y avisarte de mercados en tu zona.
            </Alert>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField select label="Departamento" value={formData.departamentoID} onChange={handleChange('departamentoID')} sx={textFieldStyles} required>
                <MenuItem value="">Seleccione...</MenuItem>
                {departamentos.map((d) => (
                  <MenuItem key={d.departamentoID} value={d.departamentoID}>{d.nombre}</MenuItem>
                ))}
              </TextField>
              <TextField select label="Municipio" value={formData.municipioID} onChange={handleChange('municipioID')} disabled={!formData.departamentoID} sx={textFieldStyles} required>
                <MenuItem value="">Seleccione...</MenuItem>
                {municipiosFiltrados.map((m) => (
                  <MenuItem key={m.municipioID} value={m.municipioID}>{m.nombre}</MenuItem>
                ))}
              </TextField>
            </Box>
            <FormControl component="fieldset">
              <FormLabel sx={{ color: '#424242', fontWeight: 600, mb: 1 }}>Zona de Residencia</FormLabel>
              <RadioGroup row value={formData.zonaResidencia} onChange={handleChange('zonaResidencia')}>
                <FormControlLabel value="Urbana" control={<Radio />} label="Urbana" />
                <FormControlLabel value="Rural" control={<Radio />} label="Rural" />
                <FormControlLabel value="Mixta" control={<Radio />} label="Mixta" />
              </RadioGroup>
            </FormControl>
          </Box>
        );
      
      case 3:
        return (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: perfil.color }}>
              💼 Perfil Laboral
            </Typography>
            <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
              <strong>Esta información</strong> nos ayuda a conectarte con cooperativas de tu sector.
            </Alert>
            <TextField label="Profesión u Oficio" placeholder="Ej: Carpintero, Diseñador, Agricultor..." value={formData.profesionOficio} onChange={handleChange('profesionOficio')} sx={{ ...textFieldStyles, mb: 2 }} fullWidth required />
            <TextField select label="Situación Laboral Actual" value={formData.ocupacionActual} onChange={handleChange('ocupacionActual')} sx={{ ...textFieldStyles, mb: 2 }} fullWidth required>
              <MenuItem value="">Seleccione...</MenuItem>
              <MenuItem value="Empleado">Empleado</MenuItem>
              <MenuItem value="Independiente">Trabajador Independiente</MenuItem>
              <MenuItem value="Desempleado">Desempleado</MenuItem>
            </TextField>
            <TextField label="Sector Económico" placeholder="Ej: Agricultura, Artesanías..." value={formData.sectorEconomico} onChange={handleChange('sectorEconomico')} sx={textFieldStyles} fullWidth />
          </Box>
        );
      
      case 4:
        return (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: perfil.color }}>
              🎯 Intereses y Expectativas
            </Typography>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel sx={{ color: '#424242', fontWeight: 600, mb: 2 }}>
                ¿En qué áreas te gustaría participar?
              </FormLabel>
              {['Agricultura y alimentación', 'Artesanías y oficios', 'Tecnología y desarrollo', 'Comercio justo'].map((area) => (
                <FormControlLabel
                  key={area}
                  control={<Checkbox checked={formData.areasInteres.includes(area)} onChange={() => handleCheckboxChange('areasInteres', area)} />}
                  label={area}
                />
              ))}
            </FormControl>
            <TextField label="¿Qué esperas de las economías colaborativas?" multiline rows={3} value={formData.queEspera} onChange={handleChange('queEspera')} sx={textFieldStyles} fullWidth />
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box>
      <Card sx={{
        borderRadius: '20px',
        mb: 3,
        background: `linear-gradient(135deg, ${perfil.color}15, ${perfil.color}30)`,
        border: `2px solid ${perfil.color}50`
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: perfil.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '28px'
            }}>
              {perfil.emoji}
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{perfil.titulo}</Typography>
              <Typography variant="body2" color="text.secondary">{perfil.subtitulo}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {perfil.beneficios.map((b, i) => (
              <Chip key={i} label={b} size="small" icon={<CheckCircleIcon />} sx={{ backgroundColor: 'white', border: `1px solid ${perfil.color}40` }} />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" fontWeight={600}>Paso {paso} de {totalPasos}</Typography>
          <Typography variant="body2" color="text.secondary">{Math.round(progreso)}%</Typography>
        </Box>
        <LinearProgress variant="determinate" value={progreso} sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: `${perfil.color}20`,
          '& .MuiLinearProgress-bar': { backgroundColor: perfil.color }
        }} />
      </Box>

      <Card sx={{ borderRadius: '20px', border: '2px solid rgba(0, 0, 0, 0.08)' }}>
        <CardContent sx={{ p: 4 }}>
          {renderPaso()}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
            <Button
              variant="outlined"
              onClick={paso === 1 ? onVolver : () => setPaso(paso - 1)}
              sx={{ borderRadius: '12px', borderWidth: '2px' }}
            >
              {paso === 1 ? 'Cancelar' : 'Anterior'}
            </Button>
            <Button
              variant="contained"
              onClick={() => paso === totalPasos ? alert('¡Completado!') : setPaso(paso + 1)}
              endIcon={paso < totalPasos ? <ArrowForwardIcon /> : null}
              sx={{
                borderRadius: '12px',
                backgroundColor: perfil.color,
                '&:hover': { backgroundColor: perfil.color, filter: 'brightness(0.9)' }
              }}
            >
              {paso === totalPasos ? 'Finalizar Registro' : 'Siguiente'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FormularioEconomiaColaborativa;